'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useQuery } from '@tanstack/react-query';

type MeResponse = {
  permissions: string[];
  role: string | null;
  roleName?: string | null;
  avatar: string | null;
  termsAcceptedAt: string | null;
  analyticsConsentAt: string | null;
  emailVerified: boolean;
  accountType: string | null;
  /** When the account was activated (email verified) — the signup moment. */
  activatedAt?: string | null;
};

class MeError extends Error {
  constructor(readonly status: number) { super(`/api/auth/me returned ${status}`); }
}

async function fetchMe(): Promise<MeResponse> {
  const res = await fetch('/api/auth/me');
  if (!res.ok) throw new MeError(res.status);
  return res.json();
}

const SESSION_REFRESH_KEY = 'session_silent_refresh';

/**
 * Drop-in replacement for next-auth's useSession, backed by Auth0.
 * Permissions come from the access token via /api/auth/me (cached by React Query).
 * Session object is memoized so its reference only changes when data actually changes.
 */
export function useSession() {
  const { user, isLoading: userLoading } = useUser();

  const { data: me, error: meError } = useQuery<MeResponse>({
    queryKey: ['session-me'],
    queryFn: fetchMe,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: (failures, error) => !(error instanceof MeError && error.status === 401) && failures < 2,
  });
  const staleCookie = !!user && meError instanceof MeError && meError.status === 401;
  const logoutAttempted = useRef(false);
  useEffect(() => {
    if (staleCookie && !logoutAttempted.current) {
      logoutAttempted.current = true;
      window.location.href = '/auth/logout';
    }
  }, [staleCookie]);

  // When the user is authenticated but the access token has no role/permissions
  // (e.g. role assigned after login by post-registration action), trigger a
  // silent re-login to get a fresh token. Uses a sessionStorage flag to avoid
  // redirect loops.
  const silentRefreshAttempted = useRef(false);
  useEffect(() => {
    if (silentRefreshAttempted.current) return;
    if (!user || !me) return;
    if (me.role !== null) return;

    // Only attempt once per browser session
    const alreadyRefreshed = sessionStorage.getItem(SESSION_REFRESH_KEY);
    if (alreadyRefreshed) return;

    silentRefreshAttempted.current = true;
    sessionStorage.setItem(SESSION_REFRESH_KEY, Date.now().toString());

    // Redirect through Auth0 login with prompt=none (silent SSO).
    // Auth0 will issue a new token with the updated RBAC permissions.
    const returnTo = window.location.pathname + window.location.search;
    window.location.href = `/auth/login?prompt=none&returnTo=${encodeURIComponent(returnTo)}`;
  }, [user, me]);

  // Stay in loading state until /api/auth/me succeeds — prevents flashing the
  // terms dialog when the endpoint transiently fails (e.g. after server restart).
  const isLoading = userLoading || (!!user && !me && !staleCookie);

  const status = isLoading
    ? 'loading'
    : user && !staleCookie
      ? 'authenticated'
      : 'unauthenticated';

  // Stable reference — only recomputes when actual data changes
  const permissions = me?.permissions ?? [];
  const role = me?.role ?? null;
  const roleName = me?.roleName ?? null;
  const termsAcceptedAt = me?.termsAcceptedAt ?? null;
  const analyticsConsentAt = me?.analyticsConsentAt ?? null;
  const permissionsKey = permissions.join(',');

  const emailVerified = me?.emailVerified ?? false;
  const accountType = me?.accountType ?? null;
  const activatedAt = me?.activatedAt ?? null;
  const resolvedAvatar = me?.avatar ?? user?.picture ?? null;

  const session = useMemo(
    () =>
      user && !staleCookie
        ? {
            user: {
              id: user.sub,
              sub: user.sub,
              name: user.name ?? null,
              email: user.email ?? null,
              image: resolvedAvatar,
              avatar: resolvedAvatar,
              emailVerified,
              accountType,
              permissions,
              role,
              roleName,
              termsAcceptedAt,
              analyticsConsentAt,
              activatedAt,
            },
            expires: '',
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.sub, user?.name, user?.email, resolvedAvatar, emailVerified, accountType, permissionsKey, role, roleName, termsAcceptedAt, analyticsConsentAt, activatedAt, staleCookie],
  );

  return { data: session, status } as const;
}
